import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateWizardLabel } from '../../../i18n/translateWizard';

function getStep3Config(subService, parentServiceTitle) {
  const name = String(subService || '').toLowerCase();
  const nameAscii = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const parentAscii = String(parentServiceTitle || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const isDebarrasMenageNettoyageConfig =
    nameAscii.includes('debarass') ||
    (nameAscii.includes('debarras') && parentAscii.includes('nettoyage'));
  if (
    nameAscii.includes('accompagn') &&
    nameAscii.includes('personne') &&
    nameAscii.includes('agee')
  ) {
    return {
      q1: 'Que souhaitez-vous comme accompagnement ?',
      q1Options: [
        'Rendez-vous médical',
        'Courses',
        'Promenade',
        'Transport',
        'Administratif',
        'Compagnie à domicile',
      ],
      q1Layout: 'grid',
      q1Single: false,
      q2: 'Durée et fréquence :',
      q2Options: ['Ponctuel', 'Régulier', "À discuter avec l'artisan"],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('garde') &&
    nameAscii.includes('stockage') &&
    (nameAscii.includes('meuble') || nameAscii.includes('meubles'))
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Stockage court terme', 'Stockage long terme', 'Garde meublée'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Durée du stockage :',
      q2Options: ["Moins d'1 mois", '1 à 3 mois', 'Plus de 3 mois', 'À discuter'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('garde') &&
    nameAscii.includes('accompagn') &&
    nameAscii.includes('enfant')
  ) {
    return {
      q1: 'Que souhaitez-vous comme service ?',
      q1Options: [
        'Garde à domicile',
        'Accompagnement scolaire',
        'Garde soir/nuit',
        'Sortie / Loisirs',
        'Aide aux devoirs',
        'Garde ponctuelle',
      ],
      q1Layout: 'grid',
      q1Single: false,
      q2: 'Âge et nombre d’enfants :',
      q2Options: ['Bébé', 'Enfant', 'Plusieurs enfants', 'À discuter'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (isDebarrasMenageNettoyageConfig) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Débarras complet', 'Débarras partiel', 'Enlèvement encombrants'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Volume à débarrasser :',
      q2Options: ['Petit volume', 'Moyen volume', 'Gros volume', 'À préciser'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (nameAscii.includes('debarras')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Débarras complet', 'Débarras partiel', 'Enlèvement encombrants'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Volume à débarrasser :',
      q2Options: ['Petit', 'Moyen', 'Grand', 'Très grand'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('organisation') &&
    nameAscii.includes('rangement') &&
    !nameAscii.includes('meuble') &&
    !nameAscii.includes('meubles')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Organisation complète', "Rangement d'une pièce", 'Tri + Débarras'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Pièce principale :',
      q2Options: ['Cuisine', 'Dressing', 'Chambre', 'Garage', 'Bureau'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('location') &&
    nameAscii.includes('utilitaire') &&
    nameAscii.includes('chauffeur')
  ) {
    return {
      q1: 'Type d’utilitaire :',
      q1Options: ['Fourgonnette', 'Camionnette', 'Camion'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Durée souhaitée :',
      q2Options: ['Demi-journée', 'Journée', 'Plusieurs jours', 'À discuter'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (nameAscii.includes('repassage')) {
    return {
      q1: 'Type de repassage :',
      q1Options: ['Vêtements', 'Linge de maison', 'Complet'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Durée estimée :',
      q2Options: ['Moins de 2h', '2 à 4h', 'Plus de 4h', 'À préciser'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (nameAscii.includes('nettoyage') && nameAscii.includes('vitre')) {
    return {
      q1: 'Que souhaitez-vous nettoyer ?',
      q1Options: ['Vitres intérieures', 'Vitres extérieures', 'Intérieur + Extérieur'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Nombre de vitres :',
      q2Options: ['Moins de 10', '10 à 20', 'Plus de 20', 'Maison complète'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('nettoyage') &&
    nameAscii.includes('tapis') &&
    nameAscii.includes('canape')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Nettoyer Canapés', 'Nettoyer Tapis', 'Les deux'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Niveau de nettoyage :',
      q2Options: ['Standard', 'En profondeur', 'Détachage'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('menage') &&
    nameAscii.includes('surface') &&
    (nameAscii.includes('meuble') || nameAscii.includes('meubles'))
  ) {
    return {
      q1: 'Que souhaitez-vous nettoyer ?',
      q1Options: [
        'Ménage complet',
        'Surfaces seulement',
        'Meubles & poussières',
        'Cuisine + SDB',
        'Grand ménage',
        'Après travaux',
      ],
      q1Layout: 'grid',
      q1Single: false,
      q2: 'Fréquence :',
      q2Options: ['Ponctuel', 'Régulier', "À discuter avec l'artisan"],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('montage') &&
    nameAscii.includes('demont') &&
    (nameAscii.includes('meuble') || nameAscii.includes('meubles'))
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Démontage', 'Montage', 'Démontage + Montage'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Type de meubles concernés : (sélection multiple possible)',
      q2Options: [
        'Meubles standards (lit, armoire, table, etc.)',
        'Meubles IKEA / en kit',
        'Meubles volumineux (dressing, bibliothèque, etc.)',
        'Meubles anciens / fragiles',
        'Bureau / Meuble de rangement',
        'Cuisine équipée',
        'Autre',
      ],
      q2Layout: 'list',
      q2Single: false,
    };
  }
  if (nameAscii.includes('chargement') && nameAscii.includes('decharg')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Chargement', 'Déchargement', 'Chargement + Déchargement'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Type de prestation souhaitée :',
      q2Options: [
        'Chargement seul',
        'Déchargement seul',
        'Chargement + Déchargement complet',
        'Chargement + Déchargement + Remontage des meubles',
      ],
      q2Layout: 'list',
      q2Single: true,
      q3: "Avez-vous besoin d'un monte-meuble ?",
      q3Options: ['Oui', 'Non', "À discuter avec l'artisan"],
      q3Single: true,
      q4: "Nombre d'étages approximatif :",
      q4Options: [
        'Rez-de-chaussée',
        '1er étage',
        '2ème étage',
        '3ème étage ou plus',
        'Avec ascenseur',
      ],
      q4Single: true,
    };
  }
  if (nameAscii.includes('vmc')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer', 'Remplacer', 'Retirer', 'Réparation'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Existe-t-il un conduit ?',
      q2Options: ['Oui', 'Non', "À discuter avec l'artisan"],
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('borne') &&
    nameAscii.includes('tableau') &&
    nameAscii.includes('electri')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Installer une station de recharge',
        'Mettre le tableau électrique aux normes',
        "Rénover l'installation électrique",
        'Câblage et tirage de câbles',
        'Installation de raccordement à la terre',
        'Autre',
      ],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Votre système électrique',
      q2Options: ['Assez récent', 'Dépassé', "À déterminer avec l'artisan"],
      q2Single: true,
    };
  }
  if (nameAscii.includes('domotique')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer', 'Supprimer et installer', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Cela concerne :',
      q2Options: ['Un appartement', 'Une maison', 'Un bureau', 'Autre'],
      q2Single: true,
    };
  }
  if (nameAscii.includes('interphone')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer', 'Remplacer', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Cela concerne :',
      q2Options: ['Un appartement', 'Une maison', 'Un bureau', 'Autre'],
      q2Single: true,
    };
  }
  if (nameAscii.includes('prise') || nameAscii.includes('interrupteur')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Nouvelle installation', 'Réparation', 'Remplacement'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: "Combien d'éléments sont impliqués ?",
      textPlaceholder: "Nombre d'éléments",
      q2: 'Est-il nécessaire de créer un nouveau circuit électrique (coupe, passage de câbles, tirage de fil,...) ?',
      q2Options: ['Oui', 'Non', "À définir avec l'artisan"],
      q2Single: true,
    };
  }
  if (nameAscii.includes('potager')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Créez un potager', 'Entretien', 'renouvellement des plantes'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la superficie du potager en m² ? (facultatif)',
      textPlaceholder: 'Exemple : 15',
    };
  }
  if (nameAscii.includes('fleur')) {
    return {
      q1: '',
      q1Options: [],
      q2: 'Combien de plants de fleurs planter ?',
      q2Options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'Plus de 10'],
      q2Select: true,
      q2Single: true,
      q2SelectPlaceholder: 'Indiquez une quantité',
      q3: 'Le sol est-il prêt à être planté ?',
      q3Options: [
        'Oui, le sol est prêt',
        "Non, aucun travail préparatoire n'est nécessaire.",
        "À discuter avec l'artisan",
      ],
      q3Single: true,
    };
  }
  if (nameAscii.includes('feuille')) {
    return {
      q1: '',
      q1Options: [],
      textLabel: 'Quelle est la superficie totale de la zone concernée en m2 ?',
      textPlaceholder: 'exemples : 17',
      q2: 'Que faut-il faire des feuilles ramassées ?',
      q2Options: ['Mettre dans sacs', 'Faire un tas', 'Évacuateur', 'Courtier', 'Un ensemble A définir'],
      q2Single: true,
    };
  }
  if (nameAscii.includes('arbust')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Planter un ou plusieurs arbustes', 'Transplanter le(s) arbuste(s)'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: "Combien d'arbustes cela concerne-t-il ?",
      textPlaceholder: 'exemple : 3',
    };
  }
  if (nameAscii.includes('clairiere')) {
    return {
      q1: '',
      q1Options: [],
      textLabel: 'Quelle est la surface approximative à déneiger (en m²) ? (facultatif)',
      textPlaceholder: 'exemple : 3',
      q2: 'Décrivez la végétation de la zone à défricher.',
      q2Options: ['Très dense', 'densité moyenne', 'Clairsemé', 'Avec des arbres', 'Avec des arbustes'],
      q2Single: true,
      q3: 'Sera-t-il nécessaire de couper ?',
      q3Options: ['Arbres', 'Arbustes', 'Plantes herbacées', "À discuter avec l'artisan"],
      q3Single: true,
    };
  }
  if (nameAscii.includes('pelouse')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Aérer une pelouse',
        'Enlever la mousse de la pelouse',
        'Désherbage',
        'Poser du gazon',
        'Semez du gazon',
        'Installer du gazon artificiel',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la superficie de pelouse à entretenir, en m² ? (facultatif)',
      textPlaceholder: 'exemple : 12',
    };
  }
  if (
    nameAscii.includes('arbre') &&
    !nameAscii.includes('abattage') &&
    !nameAscii.includes('abbatage')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Plantez un arbre', "Transplanter l'arbre(s)"],
      q1Layout: 'list',
      q1Single: true,
      textLabel: "Combien d'arbres sont concernés ?",
      textPlaceholder: 'exemple : 2',
    };
  }
  if (
    nameAscii.includes('tondre') ||
    nameAscii.includes('tonte pelouse')
  ) {
    return {
      q1: '',
      q1Options: [],
      textLabel: 'Quelle est la superficie de la pelouse en m² ? (facultatif)',
      textPlaceholder: 'exemple : 12',
      q2: "Quelle est la hauteur approximative de l'herbe ?",
      q2Options: [
        'Courte (moins de 5 cm)',
        'Moyenne (5 à 10 cm)',
        'Haute (plus de 10 cm)',
        "À définir avec l'artisan",
      ],
      q2Select: true,
      q2Single: true,
      q3: 'Faut-il refaire les bordures de la pelouse ?',
      q3Options: ['Oui', 'Non', "À définir avec l'artisan"],
      q3Single: true,
    };
  }
  if (nameAscii.includes('fenetres') && nameAscii.includes('portes-fenetres')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Rénovation', 'Suppression', 'Remplacement de vitre'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Combien de fenêtres sont concernées ?',
      textPlaceholder: 'exemple : 2',
    };
  }
  if (
    name.includes('dalle exterieur') ||
    name.includes('dalle extérieur') ||
    name.includes('dalles exterieur') ||
    name.includes('dalles extérieur') ||
    name.includes('pavage exterieur') ||
    name.includes('pavage extérieur')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Pose de pavage extérieur', 'Dépose et pose de pavage extérieur', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la surface à couvrir en m² ? (facultatif)',
      textPlaceholder: '',
      q2: 'Quel type de pavage souhaitez-vous ?',
      q2Card: true,
      q2Single: true,
      q2Options: [
        { label: 'Béton', image: '/images/beton2.webp' },
        { label: 'Pierres naturelles', image: '/images/pierresnaturelles.jpg' },
        { label: 'Carrelage/céramique', image: '/images/ceramique.jpg' },
        { label: 'Bois', image: '/images/bois.jpg' },
        { label: "À définir avec l'artisan", image: '/images/artisan.jpg' },
        { label: 'Autre', },
      ],
    };
  }
  if (
    name.includes('plaque de platre') ||
    name.includes('plaque de plâtre') ||
    name.includes('plaques de platre') ||
    name.includes('plaques de plâtre') ||
    name.includes('plaque platre') ||
    name.includes('plaque plâtre') ||
    name.includes('plaques platre') ||
    name.includes('plaques plâtre') ||
    name.includes('placo')
  ) {
    return {
      q1: 'Que souhaitez-vous construire avec des plaques de plâtre ?',
      q1Options: ['Mur', 'Plafond', 'Étagère', 'Autre'],
      q1Single: true,
    };
  }
  if (name.includes('platrage') || name.includes('plâtrage') || name.includes('platrerie') || name.includes('plâtrerie')) {
    return {
      q1: 'Quel type de travaux de plâtrerie souhaitez-vous réaliser ?',
      q1Options: [
        'Revêtement',
        'Carreaux de plâtre',
        'Travaux décoratifs',
        'Faux plafond',
        'Enduits décoratifs : stuc, personnel',
        'Revêtement extérieur',
        'Autre',
      ],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Combien de pièces sont concernées ?',
      q2Options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'Plus de 10 chambres'],
      q2Select: true,
      q2Single: true,
    };
  }
  if (name.includes('parquet')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Démontage et installation', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la surface à couvrir en m² ? (facultatif)',
      textPlaceholder: 'Exemple : 28',
    };
  }
  if (name.includes('plinthe') || name.includes('plinthes')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer', 'Remplacer', ],
      q1Layout: 'list',
      q1Single: true,
    };
  }
  if (nameAscii.includes('volet motorise')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Motoriser le volet existant', 'Démontage et installation', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
    };
  }
  if (name.includes('obturateur')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Démontage et installation', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
    };
  }
  if (name.includes('terrasse') || name.includes('terasse') || name.includes('deck')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['installation de terrasse en bois', 'Démontage et installation', 'Enlèvement uniquement', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la surface de la terrasse en m2 ?',
      textPlaceholder: 'exemple : 20',
      q2: 'Votre deck (facultatif)',
      q2Options: ['Élevé', 'Niveau du sol', "À discuter avec l'artisan"],
      q2Single: true,
    };
  }
  if (name.includes('beton poli') || name.includes('béton poli')) {
    return {
      q1: '',
      q1Options: [],
      textLabel: 'Quelle est la surface à couvrir en mètres carrés ?',
      textPlaceholder: 'Exemple : 20',
      q2: 'Le revêtement de sol existant doit-il être retiré ?',
      q2Options: ['Oui', 'Non', "À définir avec l'artisan"],
      q2Single: true,
      q3: 'Quel type de revêtement de sol est actuellement installé ?',
      q3Card: true,
      q3Single: true,
      q3Options: [
        { label: 'Béton', image: '/images/beton2.webp' },
        { label: 'Tuile', image: '/images/tuile.jpg' },
        { label: 'Bois dur', image: '/images/bois.jpg' },
        { label: 'Vinyle', image: '/images/parquet.jpg' },
        { label: 'Tapis', image: '/images/tapis.jpg' },
        { label: "À définir avec l'artisan", image: '/images/artisan.jpg' },
      ],
    };
  }
  if (name.includes('revetement de sol') || name.includes('revêtement de sol')) {
    return {
      q1: '',
      q1Options: [],
      textLabel: 'Décrivez votre projet',
      textPlaceholder: "Précisez vos besoins, la surface à traiter, l'état actuel du sol...",
      q2: 'Quel type de revêtement de sol faut-il enlever ?',
      q2Card: true,
      q2Single: true,
      q2Options: [
        { label: 'Carrelage', image: '/images/tuile.jpg' },
        { label: 'Bois dur', image: '/images/bois.jpg' },
        { label: 'Tapis', image: '/images/tapis.jpg' },
        { label: 'Vinyle', image: '/images/parquet.jpg' },
        { label: 'Autre' },
      ],
      q3: 'Faudra-t-il vider la ou les pièces ?',
      q3Options: ['Oui', 'Non', "À définir avec l'artisan"],
      q3Single: true,
    };
  }
  if (name.includes('maconnerie') || name.includes('maçonnerie')) {
    return {
      q1: 'Que faut-il faire pour votre projet ?',
      q1Options: ['Rénovation', 'Installation', 'Réparation de fissures', 'Cloisons', 'Autre'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Où souhaitez-vous effectuer votre travail ?',
      q2Options: [
        'Salon',
        'Chambre à coucher',
        'Salle de bain ou toilettes',
        'Cuisine',
        'Grenier',
        'Sous-sol',
        'Garage ou abri de jardin',
        'Extérieur',
        'Autre',
      ],
      q2Single: true,
    };
  }
  if (name.includes('assemblée') || name.includes('assemblage') ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Assembler', 'Démonter', 'Démonter et remonter'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la marque du meuble concerné ? (Facultatif)',
      textPlaceholder: 'marque de meubles',
    };
  }
  if (name.includes('etagere') || name.includes('étagère') || name.includes('étageres') || name.includes('étagères')) {
    return {
      q1: 'Quel type d’étagère souhaitez-vous installer ? Précisez la quantité.',
      q1Options: [
        'étagère murale classique',
        'étagère flottante',
        'Étagère avec rails réglables',
        'Étagère verticale',
        'Autre',
        'À définir ensemble',
      ],
    };
  }
  if (name.includes('tapis')) {
    return {
      q1: '',
      q1Options: [],
      textLabel: 'Quelle est la superficie totale en mètres carrés ?',
      textPlaceholder: 'Exemple : 20',
      q2: 'Le revêtement de sol existant doit-il être retiré ?',
      q2Options: ['Oui', 'Non', "À discuter avec l'artisan"],
      q2Single: true,
      q3: 'Quel type de revêtement de sol est actuellement installé ?',
      q3Card: true,
      q3Single: true,
      q3Options: [
        { label: 'Béton', image: '/images/beton2.webp' },
        { label: 'Tuile', image: '/images/tuile.jpg' },
        { label: 'Stratifié', image: '/images/stratifié.jpg' },
        { label: 'Vinyle', image: '/images/vinyle.jpg' },
        { label: 'Tapis', image: '/images/tapis2.jpg' },
        { label: "À discuter avec l'artisan", image: '/images/artisan.jpg' },
      ],
    };
  }
  if (name.includes('papier peint') || name.includes('papier-peint') || name.includes('revetement mural') || name.includes('revêtement mural')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer', 'Supprimer et installer', 'Retirer', 'Bande'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Quel type de revêtement mural est prévu ?',
      q2Options: ['Papier peint', 'Revêtement mural', 'Panneaux en PVC ou stratifiés', 'Revêtement texturé', 'Lambris', 'Autre'],
      q2Single: true,
    };
  }
  if (name.includes('fixation murale') || name.includes('fixationmurale')) {
    return {
      q1: "Type d'article(s) à monter, précisez la quantité :",
      q1Options: [
        'Photos, cadre(s)',
        'Miroir(s)',
        'Téléviseur(s)',
        'Objet(s) décoratif(s)',
        'Boîte(s) à clés',
        'Boîte(s) aux lettres',
        'Autre(s)',
      ],
      q1Count: true,
    };
  }
  if (name.includes('tringle a rideaux') || name.includes('tringle à rideaux') || name.includes('tringles a rideaux') || name.includes('tringles à rideaux')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer une tige', 'Remettez en place une tige existante'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Précisez le type et la quantité de tringles à rideaux :',
      q2Options: ['canne classique', 'Rail', 'Autre'],
      q2Count: true,
    };
  }
  if (
    name.includes('entretien et traitement des meubles') ||
    name.includes('entretien traitement des meubles') ||
    name.includes('traitement des meubles') ||
    name.includes('traitement meuble') ||
    name.includes('traitement de meubles')
  ) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Entretien régulier (nettoyage, protection)',
        'Traitement du bois (anti-insectes, anti-humidité)',
        'Rénovation légère (ponçage, vernis)',
        'Restauration de meuble ancien',
        'Protection (lasure, cire, vernis)',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Donnez une estimation du nombre de meubles (facultatif)',
      textPlaceholder: 'Exemple : 2 meubles',
      q2: 'Où se fera l’intervention ?',
      q2Options: ['Salon', 'Chambre', 'Cuisine', 'Salle de bain', 'Extérieur', 'Local pro', 'Autre'],
      q2Single: true,
    };
  }
  if (name.includes('reparation des meubles') || name.includes('réparation des meubles') || name.includes('reparation meuble') || name.includes('réparation meuble')) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Réparation simple (vis, fixation, ajustement)',
        'Réparation structurelle (cassure, pied, cadre…)',
        'Réparation de tiroirs / portes',
        'Réparation de charnières',
        'Restauration partielle',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Où se fera l’intervention ?',
      q2Options: ['Salon', 'Chambre', 'Cuisine', 'Bureau', 'Extérieur', 'Local pro', 'Autre'],
      q2Single: true,
    };
  }
  if (name.includes('montage des meubles') || name.includes('montage meuble')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer', 'Démontage et installation', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Où se fera l’installation des meubles ?',
      q2Options: ['Salon', 'Cuisine', 'Salle de bain', 'Extérieur', 'Local pro', 'Autre'],
      q2Single: true,
    };
  }
  if (name.trim() === 'cuisine') {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer une cuisine', 'Supprimer et installer'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la marque de la cuisine ? (facultatif)',
      textPlaceholder: 'Saisissez le nom de la marque',
    };
  }
  if (
    name.includes('meubles de cuisine') ||
    name.includes('meuble de cuisine')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer', 'Remplacer', 'Réparation'],
      q1Layout: 'grid',
      q1Single: true,
      textLabel: 'Où avez-vous acheté vos meubles de cuisine ? (facultatif)',
      textPlaceholder: '',
    };
  }
  if (
    name.includes('appareils menagers') ||
    name.includes('appareils ménagers') ||
    name.includes('appareil menager') ||
    name.includes('appareil ménager')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Installer un appareil ménager',
        'Retirer un appareil ménager',
        'Déposer et installer un appareil électroménager',
        'Réparer un appareil électroménager',
      ],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Sélectionnez le ou les appareils concernés ?',
      q2Card: true,
      q2Options: [
        { label: 'Lave-vaisselle', image: '/images/lavevaisselle.jpg' },
        { label: 'machine à laver', image: '/images/machinealaver.jpg' },
        { label: 'Séchoir', image: '/images/sechoir.jpg' },
        { label: 'Réfrigérateur', image: '/images/refrigerateur.jpg' },
        { label: 'plaque de cuisson', image: '/images/plaquedecuisson.jpg' },
        { label: 'Poêle', image: '/images/poele.jpg' },
        { label: 'Four', image: '/images/four.jpg' },
        { label: 'Capot', image: '/images/capot.jpg' },
        { label: 'Micro-ondes', image: '/images/microondes.jpg' },
        { label: 'Autre' },
      ],
    };
  }
  if (
    name.includes('hotte de cuisine') ||
    name.includes('hotte cuisine') ||
    name.includes('hottes de cuisine')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Remplacement', 'Connexion', 'Modification ou création de conduits'],
      q1Layout: 'grid',
      q1Single: true,
      q2: "De quel type de hotte s'agit-il ?",
      q2Options: ['hotte murale', 'Îlot', 'Capot intégré', 'Capot rétractable', "À déterminer avec l'artisan"],
      q2Single: true,
    };
  }
  if (name.includes('chauffe eau') || name.includes('chauffe-eau')) {
    return {
      q1: "De quel type de demande s'agit-il ?",
      q1Options: ['Installation', 'Remplacement', 'Entretien'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Quel type de chauffe-eau (facultatif)',
      q2Options: ['Électrique', 'pompe à chaleur', 'Hybride', 'Solaire', 'Autre'],
      q2Single: true,
    };
  }
  if (
    name.includes('toilette') ||
    name.includes('toilettes') ||
    name.includes('toilatette') ||
    name.includes('wc')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Réparation', 'Remplacement', 'Suppression'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Souhaiteriez-vous une citerne ?',
      q2Options: ['Visible', 'Caché', 'À définir'],
      q2Single: true,
    };
  }
  if (
    name.includes('cabine de douche') ||
    name.includes('cabine douche') ||
    name.includes('douche cabine')
  ) {
    return {
      q1: "Avez-vous besoin d'un service de montage ?",
      q1Options: ['Oui', 'Non'],
      q1Single: true,
      q2: 'Quels éléments de salle de bains faut-il assembler ?',
      q2Options: [
        'Pare-baignoire',
        'Cabine de douche',
        'Lavabo',
        'Double vasque',
        'Miroir',
        'Barre de douche',
        'porte-serviettes',
        'Autres',
      ],
      q2Layout: 'list',
      q2Single: true,
      q2ShowWhenFirstChoice: 'Oui',
    };
  }
  if (
    name.includes('eclairage') ||
    name.includes('éclairage')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer', 'Remplacer', 'Réparation'],
      q1Layout: 'grid',
      q1Single: true,
      q2: "Type d'article concerné, précisez la quantité :",
      q2Count: true,
      q2Options: [
        'Applique murale',
        'plafonnier',
        'Mettre en lumière',
        'ampoule',
        'Lustre',
        'Autres',
      ],
    };
  }
  if (
    name.includes('meubles et rangement') ||
    name.includes('meuble et rangement') ||
    (name.includes('meubles') && name.includes('rangement') && name.includes('salle de bain'))
  ) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Installation de meubles de salle de bain',
        'Création de rangements',
        'Optimisation de l’espace',
        'Remplacement de meubles existants',
        'Aménagement complet',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Donnez une estimation du nombre d’éléments (facultatif)',
      textPlaceholder: 'Exemple : 2 meubles',
      q2: 'Type de salle de bain :',
      q2Options: [
        'Salle de bain principale',
        'Salle de bain secondaire',
        'Petite salle d’eau',
        'Salle de bain professionnelle',
        'Autre',
      ],
      q2Single: true,
    };
  }
  if (name.includes('mastic')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Déposer et installer les joints', 'Installer uniquement les joints'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Quels sont les éléments concernés ?',
      q2Card: true,
      q2Options: [
        { label: 'Toilettes', image: '/images/toilettes.jpg' },
        { label: 'Douche', image: '/images/douche.jpg' },
        { label: 'Évier / Lavabo', image: '/images/lavabo.jpg' },
        { label: 'Baignoire', image: '/images/baignoire.jpg' },
        { label: 'Comptoir', image: '/images/comptoir.jpg' },
        { label: 'Carrelage', image: '/images/carelage.jpg' },
        { label: "À définir avec l'artisan", image: '/images/artisan.jpg' },
      ],
    };
  }
  if (name.includes('robinetterie')) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Installation d’un robinet de cuisine',
        'Réparation (fuite, dysfonctionnement)',
        'Remplacement du robinet',
        'Entretien / détartrage',
        'Réglage de pression',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Donnez une estimation du nombre de robinets (facultatif)',
      textPlaceholder: 'Exemple : 1 robinet',
      q2: 'Où se fera l’intervention ?',
      q2Options: ['Cuisine principale', 'Cuisine secondaire', 'Cuisine professionnelle', 'Autre'],
      q2Single: true,
    };
  }
  if (
    name.includes('espace rangement') ||
    name.includes('espace de rangement') ||
    name.includes('rangements cuisine') ||
    name.includes('rangement cuisine')
  ) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Création d’espace de rangement',
        'Optimisation des rangements existants',
        'Installation de meubles de cuisine',
        'Aménagement intérieur (tiroirs, étagères…)',
        'Organisation fonctionnelle',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Donnez une estimation du nombre d’éléments (facultatif)',
      textPlaceholder: 'Exemple : 3 rangements',
      q2: 'Où se fera l’intervention ?',
      q2Options: ['Cuisine principale', 'Cuisine secondaire', 'Cuisine professionnelle', 'Autre'],
      q2Single: true,
    };
  }
  if (
    name.includes('salle de bain') ||
    name.includes("salle d'eau") ||
    name.includes('salle de bains')
  ) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Installation (équipements neufs)',
        'Rénovation complète',
        'Réparation (fuite, casse…)',
        'Remplacement d’équipements',
        'Aménagement / optimisation de l’espace',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la surface approximative ? (facultatif)',
      textPlaceholder: 'Exemple : 6 m²',
      q2: 'Où se fera l’intervention ?',
      q2Options: [
        'Salle de bain principale',
        'Salle de bain secondaire',
        'Salle d’eau',
        'Local professionnel',
        'Autre',
      ],
      q2Single: true,
    };
  }
  if (name.includes('climatisation')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Remplacement', 'Réparation', 'Entretien'],
      q1Layout: 'list',
      q1Single: true,
    };
  }
  if (name.includes('chauffage')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Supprimer et installer', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
    };
  }
  if (name.includes('chaudi')) {
    return {
      q1: 'De quoi avez-vous besoin ?',
      q1Options: ['Installation', 'Remplacement', 'Autre'],
      q1Layout: 'list',
      q1Single: true,
    };
  }
  if (nameAscii.includes('batterie') && nameAscii.includes('elect')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Remplacement batterie', 'Diagnostic', 'Recharge / Dépannage'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Type de batterie :',
      q2Options: ['Batterie 12V', 'Batterie VE (Haute tension)', 'Hybride'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (nameAscii.includes('plaquette') && nameAscii.includes('frein')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Plaquette avant','Plaquette arrière','Avant + Arrière','Révision freins complète',
      ],
      q1Layout: 'list',
      q1Single: true,
    };
  }
  if (
    (nameAscii.includes('depannage') || nameAscii.includes('depan')) &&
    (nameAscii.includes('remorquage') || nameAscii.includes('remorqu'))
  ) {
    return {
      q1: 'Que souhaitez-vous ?',
      q1Options: ['Dépannage sur place', 'Remorquage', 'Les deux'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Type de véhicule :',
      q2Options: ['Voiture', 'Utilitaire', 'Moto', 'Autre'],
      q2Layout: 'grid',
      q2GridVariant: 'type-vehicule',
      q2Single: true,
    };
  }
  if (
    (nameAscii.includes('diagnost') || nameAscii.includes('diagnostique')) &&
    nameAscii.includes('nettoy')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Diagnostic électronique',
        'Nettoyage moteur',
        'Nettoyage climatisation',
        'Nettoyage intérieur',
        'Diagnostic + Nettoyage',
        'Autre',
      ],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Type de véhicule :',
      q2Options: ['Voiture', 'Utilitaire', 'Autre'],
      q2Layout: 'grid',
      q2GridVariant: 'type-vehicule',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('changement') &&
    nameAscii.includes('piece') &&
    nameAscii.includes('auto')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Changement pièce mécanique',
        'Changement pièce électrique',
        "Changement pièce d'usure",
        'Changement plusieurs pièces',
      ],
      q1Layout: 'grid',
      q1GridVariant: 'type-vehicule',
      q1Single: true,
      q2: 'Type de véhicule :',
      q2Options: ['Voiture', 'SUV', 'Utilitaire', 'Moto'],
      q2Layout: 'grid',
      q2GridVariant: 'type-vehicule',
      q2Single: true,
    };
  }
  if (nameAscii.includes('changement') && nameAscii.includes('roue')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Changement saisonnier',
        'Changement 4 roues',
        'Réparation crevaison',
        'Montage + Équilibrage',
        'Géométrie',
      ],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Type de véhicule :',
      q2Options: ['Voiture', 'SUV', 'Utilitaire', 'Moto'],
      q2Layout: 'grid',
      q2GridVariant: 'type-vehicule',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('entretien') &&
    nameAscii.includes('reparation') &&
    !nameAscii.includes('meuble') &&
    !nameAscii.includes('depannage')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Entretien',
        'Réparation',
        'Révision',
        'Diagnostic',
        'Dépannage urgent',
      ],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Type de véhicule :',
      q2Options: ['Voiture', 'Utilitaire', 'Moto', 'Autre'],
      q2Layout: 'grid',
      q2GridVariant: 'type-vehicule',
      q2Single: true,
    };
  }
  if (
    name.includes('entretien et depannage') ||
    name.includes('entretien et dépannage') ||
    (name.includes('entretien') && name.includes('depannage')) ||
    (name.includes('entretien') && name.includes('dépannage'))
  ) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Entretien régulier',
        'Dépannage (réparation rapide)',
        'Recherche de fuite',
        'Débouchage',
        'Vérification générale',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Donnez une estimation des éléments concernés (facultatif)',
      textPlaceholder: 'Exemple : 2 équipements',
      q2: 'Où se fera l’intervention ?',
      q2Options: ['Cuisine', 'Salle de bain', 'WC', 'Extérieur', 'Local professionnel', 'Autre'],
      q2Single: true,
    };
  }
  if (name.includes('tuyaux') || name.trim() === 'tuyau') {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Installer un système de plomberie',
        'Déplacer les tuyaux',
        'Réparer la pièce défectueuse',
        'Autre',
      ],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Quel élément nécessite une intervention (facultatif) ?',
      q2Card: true,
      q2Single: false,
      q2GridVariant: 'tuyaux-elements',
      q2Options: [
        { label: 'Tuyaux', image: '/images/tuyaux.jpg' },
        { label: 'Toilettes', image: '/images/toilettes.jpg' },
        { label: 'Évier / Bassin', image: '/images/evier.jpg' },
        { label: 'Douche / Bain', image: '/images/bain.jpg' },
        { label: 'Lave-vaisselle', image: '/images/lavevaisselle.jpg' },
        { label: 'Machine à laver', image: '/images/machinealaver.jpg' },
        { label: 'Autre' },
      ],
    };
  }
  if (name.includes('plomberie')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Éléments d’installation',
        'Déboucher les canalisations',
        'Détartrage des canalisations',
        'Purger les radiateurs',
        'Autre',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Donnez une estimation du nombre d’éléments concernés (facultatif)',
      textPlaceholder: 'Exemple : 1 évier',
      q2: 'Quelles sont les chambres concernées ?',
      q2Options: ['Cuisine', 'Salle de bain', 'Salon', 'De plein air', 'Autre'],
      q2Single: false,
    };
  }
  if (    
    name.includes('meuble sur mesure') ||
    name.includes('meubles sur mesure') ||
    name.includes('sur mesure')
  ) {
    return {
      q1: 'Quel type de meubles aimeriez-vous créer ?',
      q1Options: [
        'Garde-robe',
        'Buffet',
        'Literie',
        'Tableau',
        'Chaises ou fauteuils',
        'Canapé',
        'Meubles de rangement',
        'Étagères / bibliothèques',
        'Autre',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la longueur approximative du meuble à réaliser, en cm ? (facultatif)',
      textPlaceholder: '',
      q2: 'Quelle est la hauteur approximative du meuble à créer, en cm ? (facultatif)',
      q2Options: [],
      q3: 'Quelle est la profondeur du meuble à créer, en cm ? (facultatif)',
      q3Options: [],
    };
  }
  if (name.includes('carrelage') || name.includes('carreau')) {
    return {
      q1: 'Les carreaux doivent être posés',
      q1Options: ['Sur le sol', 'Sur le mur', 'Autre'],
      textLabel: 'Quelle est la surface à carreler en m² ?',
      textPlaceholder: 'Exemple : 10',
      q2: 'Combien de pièces sont concernées ? (facultatif)',
      q2Options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'Plus de 10 chambres'],
      q3: 'Le revêtement mural ou de sol existant doit-il être retiré au préalable ?',
      q3Options: ['Oui', 'Non', "À définir avec l'artisan"],
    };
  }
  if (
    name.includes('decoration murale et accessoires') ||
    name.includes('décoration murale et accessoires') ||
    name.includes('decoration murale') ||
    name.includes('décoration murale')
  ) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Installation de décoration murale',
        'Pose de cadres / tableaux',
        'Installation de miroirs',
        'Pose d’étagères décoratives',
        'Aménagement décoratif complet',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Donnez une estimation du nombre d’éléments (facultatif)',
      textPlaceholder: 'Exemple : 3 éléments',
      q2: 'Où se fera l’intervention ?',
      q2Options: ['Salon', 'Chambre', 'Cuisine', 'Salle de bain', 'Couloir / Entrée', 'Bureau / Local pro', 'Autre'],
      q2Single: true,
    };
  }
  if (
    name.includes('decoration et amenagement des espaces') ||
    name.includes('décoration et aménagement des espaces') || 
    name.includes('amenagement des espaces') ||
    name.includes('aménagement des espaces')
  ) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Décoration d’un espace',
        'Réaménagement (optimisation de l’espace)',
        'Relooking complet',      
        'Conseils en décoration',
        'Aménagement après déménagement',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la surface approximative ? (facultatif)',
      textPlaceholder: 'Exemple : 20 m²',
      q2: 'Quel type d’espace ?',
      q2Options: ['Salon', 'Chambre', 'Cuisine', 'Salle de bain', 'Bureau', 'Local commercial', 'Autre'],
      q2Single: true,
    };
  }
  if (
    name.includes('eclairage et home staging') ||
    name.includes('éclairage et home staging') ||
    name.includes('eclairage home staging') ||
    name.includes('éclairage home staging')
  ) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Installation / amélioration de l’éclairage',
        'Conseils en éclairage',
        'Home staging (valorisation pour vente/location)',
        'Réaménagement avec éclairage décoratif',
        'Optimisation de l’ambiance lumineuse',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la surface approximative ? (facultatif)',
      textPlaceholder: 'Exemple : 25 m²',
      q2: 'Quel type d’espace ?',
      q2Options: ['Salon', 'Chambre', 'Cuisine', 'Salle de bain', 'Bureau', 'Local commercial', 'Autre'],
      q2Single: true,
    };
  }
  if (
    name.includes('conseil en decoration') ||
    name.includes('conseil en décoration') ||
    name.includes('conseils en decoration') ||
    name.includes('conseils en décoration')
  ) {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Conseils en décoration intérieure',
        'Choix des couleurs',
        'Choix du mobilier',
        'Optimisation de l’espace',
        'Harmonisation du style',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la surface approximative ? (facultatif)',
      textPlaceholder: 'Exemple : 20 m²',
      q2: 'Quel type d’espace ?',
      q2Options: ['Salon', 'Chambre', 'Cuisine', 'Salle de bain', 'Bureau', 'Local commercial', 'Autre'],
      q2Single: true,
    };
  }
  if (name.includes('peinture') || name.includes('mur') || name.includes('plafond')) {
    return {
      q1: 'Quelles surfaces doivent être peintes ?',
      q1Options: ['Mur', 'Plafond', 'Autre', 'À définir ensemble'],
      textLabel: 'Quelle est la surface à peindre en m² ? (facultatif)',
      textPlaceholder: 'Exemple : 20',
      q2: 'Quelles sont les chambres concernées ?',
      q2Options: ['Salon', 'Chambre à coucher', 'Couloir', 'Cuisine', 'Salle de bain', 'Grenier', 'Autre'],
    };
  }

  if (nameAscii.includes('portail motorise')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Motoriser le portail existant', 'Démontage et installation', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la hauteur du portail en cm ? (facultatif)',
      textPlaceholder: 'exemple : 175',
      q2: 'Quelle est la largeur du portail en cm ? (facultatif)',
      q2Options: [],
      q2TextPlaceholder: 'exemple : 300',
    };
  }
  if (name.trim() === 'portail') {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Démontage et installation', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la hauteur du portail en cm ? (facultatif)',
      textPlaceholder: 'Exemple : 200',
      q2: 'Quelle est la largeur du portail en cm ? (facultatif)',
      q2Options: [],
      q2TextPlaceholder: 'Exemple : 350',
    };
  }

  if (name.trim() === 'vestiaire') {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installer', 'Démonter', 'Remplacer', 'Ajuster'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Quelle est la longueur totale du dressing ?',
      q2Options: [
        "Moins d'1 mètre",
        'Entre 1 et 2 mètres',
        'Entre 2 et 3 mètres',
        'Plus de 3 mètres',
        "À définir avec l'artisan",
      ],
      q2Select: true,
      q2Single: true,
      q3: 'Quelle est la forme du dressing (facultatif) ?',
      q3Options: ['I', 'L', 'II', 'U', 'Autre'],
      q3Single: true,
    };
  }

  if (nameAscii.includes('store motorise') || nameAscii.includes('stores motorise')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Remplacement', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Vos stores sont',
      q2Card: true,
      q2Single: true,
      q2GridVariant: 'store-motorise',
      q2Options: [
        { label: 'Rouleau', image: '/images/rouleau.jpg' },
        { label: 'Glissement', image: '/images/glissement.jpg' },
        { label: 'Charnière', image: '/images/charniere.jpg' },
        { label: 'Autre' },
        { label: "À définir avec l'artisan", image: '/images/artisan.jpg' },
      ],
    };
  }
  if (name.trim() === 'stores') {
    return {
      q1: 'De quoi avez-vous besoin ?',
      q1Options: [
        'Installation',
        'Remplacement',
      ],
      q1Layout: 'list',
      q1Single: true,
  
    };
  }
  if (nameAscii.includes('garage motorise')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Installation',
        'Motoriser une porte existante',
        'Démontage et installation',
        'Réparation',
        'Entretien',
      ],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Type de porte de garage :',
      q2Card: true,
      q2Single: true,
      q2GridVariant: 'garage-door',
      q2Options: [
        { label: 'En haut et par-dessus', image: '/images/garageenhaut.jpg' },
        { label: 'Charnière', image: '/images/charniere.jpg' },
        { label: 'Pliant', image: '/images/pliant.jpg' },
        { label: 'Glissement', image: '/images/glissement.jpg' },
        { label: 'En coupe', image: '/images/encoupe.jpg' },
        { label: 'Rouleau', image: '/images/rouleau.jpg' },
        { label: "À définir avec l'artisan", image: '/images/artisan.jpg' },
      ],
    };
  }
  if (
    name.includes('porte de garage') ||
    name.includes('portes de garage') ||
    name.includes('garage door')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation', 'Démontage et installation', 'Réparation'],
      q1Layout: 'list',
      q1Single: true,
      q2: 'Type de porte de garage',
      q2Card: true,
      q2Single: true,
      q2GridVariant: 'garage-door',
      q2Options: [
        { label: 'En haut et par-dessus',image: '/images/garageenhaut.jpg' },
        { label: 'Charnière',image: '/images/charniere.jpg' },
        { label: 'Pliant',image: '/images/pliant.jpg' },
        { label: 'Glissement',image: '/images/glissement.jpg' },
        { label: 'En coupe',image: '/images/encoupe.jpg' },
        { label: 'Rouleau',image: '/images/rouleau.jpg' },
        { label: "À définir avec l'artisan",image: '/images/artisan.jpg' },
      ],
    };
  }
  if (
    (nameAscii.includes('abattage') || nameAscii.includes('abbatage')) &&
    nameAscii.includes('arbre')
  ) {
    return {
      q1: 'Quelle est la nature de votre demande ?',
      q1Options: [
        'Élaguer un arbre',
        'Abattre un arbre',
        'Découper un arbre',
        "Enlever une souche d'arbre",
        'Autre',
      ],
      q1Layout: 'list',
      q1Single: true,
    };
  }
  if (
    name.includes('espace vert') ||
    name.includes('espaces verts') ||
    name.includes('espace verts')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Élaguer les arbres',
        'Tondez la pelouse',
        'Traiter les plantes ou les arbustes',
        'Enlever les mauvaises herbes',
        'Ramassez les feuilles',
        'Autre',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Quelle est la surface à maintenir en m² ? (facultatif)',
      textPlaceholder: 'exemple : 10',
    };
  }
  if (name.includes('haie') || name.includes('haies')) {
    return {
      q1: 'Quel type de haie souhaitez-vous tailler ?',
      q1Card: true,
      q1GridVariant: 'haies',
      q1Single: true,
      q1Options: [
        { label: 'Haie légère', image: '/images/haielegere.jpg' },
        { label: 'Haie dense/compacte', image: '/images/haiedense.jpg' },
        { label: 'Haie mixte', image: '/images/haiemixte.jpg' },
        { label: 'Arbustes isolés', image: '/images/arbustesisoles.jpg' },
        { label: "À définir avec l'artisan", image: '/images/artisan.jpg' },
      ],
      q2: 'Quelle est la hauteur de la haie ? (facultatif)',
      q2Options: [
        'Moins de 1 m',
        'Entre 1 m et 2 m',
        'Entre 2 m et 3 m',
        'Plus de 3 m',
        "À définir avec l'artisan",
      
      ],
    
      q2Select: true,
      q2Single: true,
      q3: 'Quelle est la longueur de la haie en cm ? (facultatif)',
      q3Options: [],
      q3Single: true,
    };
  }
  if (nameAscii.includes('ouverture') && nameAscii.includes('porte')) {
    return {
      q1: 'Que souhaitez-vous ?',
      q1Options: [
        'Ouverture porte appartement',
        'Ouverture porte maison',
        'Ouverture voiture',
        'Ouverture garage',
      ],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Urgence :',
      q2Options: ['Immédiate', 'Aujourd’hui', 'À convenir'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (
    nameAscii.includes('porte') &&
    nameAscii.includes('securis') &&
    !nameAscii.includes('ouverture')
  ) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: ['Installation porte blindée', 'Remplacement', 'Renforcement'],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Niveau de sécurité :',
      q2Options: ['Standard', 'Haute sécurité', 'Très haute sécurité'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (name.trim() === 'porte') {
    return {
      q1: 'Que souhaitez-vous réaliser ?',
      q1Options: [
        'Installation de porte',
        'Remplacement de porte',
        'Réparation (charnière, poignée…)',
        'Réglage / ajustement',
        'Pose de serrure',
        'À définir ensemble',
      ],
      q1Layout: 'list',
      q1Single: true,
      textLabel: 'Donnez une estimation du nombre de portes (facultatif)',
      textPlaceholder: 'Exemple : 1 porte',
      q2: 'Où se fera l’intervention ?',
      q2Options: ['Entrée principale', 'Chambre', 'Salle de bain', 'Cuisine', 'Bureau / Local pro', 'Autre'],
      q2Single: true,
    };
  }
  if (nameAscii.includes('alarme') && !nameAscii.includes('camera')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Installation alarme',
        'Réparation alarme',
        'Extension',
        'Maintenance',
        'Alarme avec caméra',
      ],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Type d’alarme :',
      q2Options: ['Sans fil', 'Avec caméra', 'Complète'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (nameAscii.includes('camera') && !nameAscii.includes('alarme')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Installation caméras',
        'Réparation caméras',
        'Extension système',
        'Configuration',
        'Maintenance',
      ],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Type de caméras :',
      q2Options: ['Intérieures', 'Extérieures', 'Intérieur + Extérieur'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (nameAscii.includes('serrurerie')) {
    return {
      q1: 'Que souhaitez-vous faire ?',
      q1Options: [
        'Ouverture porte',
        'Changement serrure',
        'Réparation',
        'Installation nouvelle',
        'Porte blindée',
      ],
      q1Layout: 'grid',
      q1Single: true,
      q2: 'Urgence :',
      q2Options: ['Immédiate', 'Aujourd’hui', 'Programmée'],
      q2Layout: 'grid',
      q2Single: true,
    };
  }
  if (name.includes('porte') || name.includes('serrurerie')) {
    return {
      q1: 'Quel est votre besoin principal ?',
      q1Options: ['Dépannage urgence', 'Installation', 'Remplacement', 'À définir ensemble'],
      textLabel: 'Combien d’éléments sont concernés ? (facultatif)',
      textPlaceholder: 'Exemple : 2',
      q2: 'Type d’intervention souhaitée',
      q2Options: ['Porte d’entrée', 'Porte intérieure', 'Serrure', 'Alarme', 'Caméra', 'Autre'],
    };
  }

  return {
    q1: 'Que souhaitez-vous réaliser ?',
    q1Options: ['Réparation', 'Installation', 'Entretien', 'À définir ensemble'],
    textLabel: 'Donnez une estimation de la quantité / surface (facultatif)',
    textPlaceholder: 'Exemple : 30',
    q2: 'Où se fera l’intervention ?',
    q2Options: ['Salon', 'Cuisine', 'Salle de bain', 'Extérieur', 'Local pro', 'Autre'],
  };
}

function RequestDemandDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const { t } = useTranslation();
  const tw = (text) => translateWizardLabel(t, text);

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const parentServiceTitle = location.state?.parentServiceTitle || t('common.service');
  const projectTitle = location.state?.projectTitle || '';
  const subServiceNeedle = String(selectedSubService || '').toLowerCase();
  const subServiceNeedleAscii = subServiceNeedle.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const step3Config = useMemo(
    () => getStep3Config(selectedSubService, parentServiceTitle),
    [selectedSubService, parentServiceTitle]
  );
  const isVmcService = subServiceNeedleAscii.includes('vmc');
  const isChargementDechargementService =
    subServiceNeedleAscii.includes('chargement') && subServiceNeedleAscii.includes('decharg');
  const isMontageDemontageMeublesService =
    subServiceNeedleAscii.includes('montage') &&
    subServiceNeedleAscii.includes('demont') &&
    (subServiceNeedleAscii.includes('meuble') || subServiceNeedleAscii.includes('meubles'));
  const isAccompagnementPersonnesAgeesService =
    subServiceNeedleAscii.includes('accompagn') &&
    subServiceNeedleAscii.includes('personne') &&
    subServiceNeedleAscii.includes('agee');
  const isGardeStockageMeublesService =
    subServiceNeedleAscii.includes('garde') &&
    subServiceNeedleAscii.includes('stockage') &&
    (subServiceNeedleAscii.includes('meuble') || subServiceNeedleAscii.includes('meubles'));
  const isGardeAccompagnementEnfantsService =
    subServiceNeedleAscii.includes('garde') &&
    subServiceNeedleAscii.includes('accompagn') &&
    subServiceNeedleAscii.includes('enfant');
  const isDebarrasMenageNettoyageService =
    subServiceNeedleAscii.includes('debarass') ||
    (subServiceNeedleAscii.includes('debarras') &&
      String(parentServiceTitle || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes('nettoyage'));
  const isDebarrasService =
    subServiceNeedleAscii.includes('debarras') && !isDebarrasMenageNettoyageService;
  const isLocationUtilitaireAvecChauffeurService =
    subServiceNeedleAscii.includes('location') &&
    subServiceNeedleAscii.includes('utilitaire') &&
    subServiceNeedleAscii.includes('chauffeur');
  const isMenageSurfaceMeublesService =
    subServiceNeedleAscii.includes('menage') &&
    subServiceNeedleAscii.includes('surface') &&
    (subServiceNeedleAscii.includes('meuble') || subServiceNeedleAscii.includes('meubles'));
  const isRepassageService = subServiceNeedleAscii.includes('repassage');
  const isNettoyageVitresService =
    subServiceNeedleAscii.includes('nettoyage') && subServiceNeedleAscii.includes('vitre');
  const isNettoyageTapisCanapesService =
    subServiceNeedleAscii.includes('nettoyage') &&
    subServiceNeedleAscii.includes('tapis') &&
    subServiceNeedleAscii.includes('canape');
  const isOrganisationRangementService =
    subServiceNeedleAscii.includes('organisation') &&
    subServiceNeedleAscii.includes('rangement') &&
    !subServiceNeedleAscii.includes('meuble') &&
    !subServiceNeedleAscii.includes('meubles');
  const isOuverturePorteService =
    subServiceNeedleAscii.includes('ouverture') &&
    subServiceNeedleAscii.includes('porte');
  const isPortesSecuriseesService =
    subServiceNeedleAscii.includes('porte') &&
    subServiceNeedleAscii.includes('securis') &&
    !subServiceNeedleAscii.includes('ouverture');
  const isHaiesService = subServiceNeedle.includes('haie');
  const isFeuillesService = subServiceNeedleAscii.includes('feuille');
  const isParquetService = subServiceNeedle.includes('parquet');
  const isMasticService = subServiceNeedle.includes('mastic');
  const isHomeApplianceService =
    subServiceNeedle.includes('appareils menagers') ||
    subServiceNeedle.includes('appareils ménagers') ||
    subServiceNeedle.includes('appareil menager') ||
    subServiceNeedle.includes('appareil ménager');

  const [firstChoices, setFirstChoices] = useState([]);
  const [firstChoiceCounts, setFirstChoiceCounts] = useState({});
  const [secondChoices, setSecondChoices] = useState([]);
  const [secondChoiceCounts, setSecondChoiceCounts] = useState({});
  const [thirdChoices, setThirdChoices] = useState([]);
  const [fourthChoices, setFourthChoices] = useState([]);
  const [quantityValue, setQuantityValue] = useState('');

  const toggleChoice = (value, setState) => {
    setState((prev) => (prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]));
  };
  const chooseSingle = (value, setState) => {
    setState([value]);
  };
  const updateCountChoice = (value, delta) => {
    setFirstChoiceCounts((prev) => {
      const current = prev[value] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [value]: next };
    });
  };
  const updateSecondCountChoice = (value, delta) => {
    setSecondChoiceCounts((prev) => {
      const current = prev[value] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [value]: next };
    });
  };
  const getOptionValue = (option) => (typeof option === 'string' ? option : option.label);
  const getOptionImage = (option) => (typeof option === 'string' ? '' : option.image || '');
  const shouldShowQ2 =
    !step3Config.q2ShowWhenFirstChoice ||
    firstChoices.includes(step3Config.q2ShowWhenFirstChoice);

  useEffect(() => {
    if (isParquetService && firstChoices.length === 0) {
      setFirstChoices(['Installation']);
    }
  }, [isParquetService, firstChoices.length]);

  return (
    <AppLayout title={t('wizard.step3Title')} rootClass="layout-1">
      <style>{`
        .request-step3-page {
          background: #f2f3f5;
          min-height: 78vh;
          border-radius: 14px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .request-step3-content {
          flex: 1 1 auto;
          width: 100%;
          max-width: 760px;
          margin: 0 auto;
          padding-top: 4.2rem;
          padding-bottom: 2rem;
        }
        .request-step3-step {
          color: #697586;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }
        .request-step3-heading {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 2rem;
        }
        .request-step3-question {
          color: #0f2b3d;
          font-size: 1.05rem;
          font-weight: 500;
          margin-bottom: 1rem;
        }
        .request-step3-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.75rem;
        }
        .request-step3-grid--type-vehicule {
          display: flex;
          flex-wrap: nowrap;
          gap: 0.75rem;
        }
        .request-step3-grid--type-vehicule .request-step3-choice {
          flex: 1 1 0;
          min-width: 0;
        }
        .request-step3-grid--tuyaux-elements {
          gap: 1.1rem;
        }
        .request-step3-grid--garage-door {
          gap: 0.9rem;
        }
        .request-step3-grid--garage-door .request-step3-card {
          min-height: 156px;
          display: flex;
          flex-direction: column;
        }
        .request-step3-grid--garage-door .request-step3-card img {
          height: 82px;
          object-fit: contain;
          background: #f8fafc;
        }
        .request-step3-grid--garage-door .request-step3-card-label {
          margin-top: auto;
          font-size: 0.86rem;
        }
        .request-step3-grid--haies {
          gap: 0.9rem;
        }
        .request-step3-grid--haies .request-step3-card {
          min-height: 164px;
          display: flex;
          flex-direction: column;
        }
        .request-step3-grid--haies .request-step3-card img {
          height: 88px;
          object-fit: contain;
          background: #f8fafc;
        }
        .request-step3-grid--haies .request-step3-card-label {
          margin-top: auto;
          font-size: 0.88rem;
        }
        .request-step3-grid--store-motorise {
          gap: 0.9rem;
        }
        .request-step3-grid--store-motorise .request-step3-card {
          min-height: 152px;
          display: flex;
          flex-direction: column;
        }
        .request-step3-grid--store-motorise .request-step3-card img,
        .request-step3-grid--store-motorise .request-step3-card .request-step3-card-visual {
          height: 84px;
          object-fit: contain;
          background: #f8fafc;
        }
        .request-step3-grid--store-motorise .request-step3-card-label {
          margin-top: auto;
          font-size: 0.88rem;
        }
        .request-step3-grid--tuyaux-elements .request-step3-card {
          display: flex;
          flex-direction: column;
          padding: 1rem 1rem 0.85rem;
          min-height: 158px;
          border-radius: 10px;
          border-color: #e0e4e8;
        }
        .request-step3-grid--tuyaux-elements .request-step3-card img {
          width: 100%;
          height: 92px;
          object-fit: contain;
          background: #f8fafc;
          margin-bottom: 0.65rem;
        }
        .request-step3-grid--tuyaux-elements .request-step3-card .request-step3-card-visual {
          width: 100%;
          height: 92px;
          margin-bottom: 0.65rem;
        }
        .request-step3-grid--tuyaux-elements .request-step3-card-label {
          margin-top: auto;
          text-align: left;
          font-weight: 700;
        }
        .request-step3-card-visual {
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: #f8fafc;
          color: #64748b;
          font-size: 1.35rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          line-height: 1;
        }
        .request-step3-list {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }
        .request-step3-choice {
          min-height: 52px;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          color: #0f2b3d;
          text-align: left;
          padding: 0.75rem 0.9rem;
          font-weight: 500;
        }
        .request-step3-count-list {
          display: grid;
          gap: 0.6rem;
        }
        .request-step3-count-item {
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          min-height: 52px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.7rem;
          padding: 0.75rem 0.85rem;
          color: #0f2b3d;
          font-weight: 500;
        }
        .request-step3-count-item.is-active {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
        }
        .request-step3-count-controls {
          display: flex;
          align-items: center;
          gap: 0.35rem;
        }
        .request-step3-count-btn {
          width: 20px;
          height: 20px;
          border: none;
          border-radius: 4px;
          background: #2563eb;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          padding: 0;
        }
        .request-step3-count-btn:disabled {
          background: #9db6ee;
          cursor: not-allowed;
        }
        .request-step3-count-value {
          min-width: 1.2rem;
          text-align: center;
          font-weight: 600;
        }
        .request-step3-card {
          border: 1px solid #d6dde5;
          border-radius: 12px;
          background: #fff;
          text-align: left;
          padding: 0.6rem;
          min-height: 138px;
          transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
        }
        .request-step3-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 22px rgba(15, 43, 61, 0.08);
        }
        .request-step3-card img {
          width: 100%;
          height: 76px;
          object-fit: cover;
          border-radius: 8px;
          margin-bottom: 0.6rem;
        }
        .request-step3-card--appliance img {
          height: 76px;
          object-fit: contain;
          background: #f8fafc;
        }
        .request-step3-card--mastic img {
          height: 82px;
          object-fit: contain;
          background: #f8fafc;
          padding: 0.2rem;
        }
        .request-step3-card-label {
          color: #0f2b3d;
          font-weight: 600;
          font-size: 0.9rem;
          line-height: 1.35;
        }
        .request-step3-card.is-active {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12), 0 8px 22px rgba(37, 99, 235, 0.15);
        }
        .request-step3-choice.is-active {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
        }
        .request-step3-input {
          width: 100%;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          height: 44px;
          padding: 0 0.8rem;
          color: #0f2b3d;
          background: #fff;
        }
        .request-step3-footer {
          flex-shrink: 0;
          margin-top: auto;
          border-top: 1px solid #dde3ea;
          padding: 0.7rem 1.1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fb;
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          z-index: 5;
          box-shadow: 0 -4px 16px rgba(15, 43, 61, 0.08);
        }
        @media (max-width: 992px) {
          .request-step3-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .request-step3-grid--tuyaux-elements {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
          .request-step3-grid--garage-door {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
          .request-step3-grid--haies {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
          .request-step3-grid--store-motorise {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        @media (max-width: 575px) {
          .request-step3-grid {
            grid-template-columns: 1fr;
          }
          .request-step3-grid.request-step3-grid--type-vehicule {
            grid-template-columns: unset;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 4px;
            margin-left: -2px;
            margin-right: -2px;
            padding-left: 2px;
            padding-right: 2px;
          }
          .request-step3-grid.request-step3-grid--type-vehicule .request-step3-choice {
            flex: 0 0 auto;
            min-width: 6.25rem;
          }
          .request-step3-grid--tuyaux-elements {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 0.55rem;
          }
          .request-step3-grid--tuyaux-elements .request-step3-card {
            padding: 0.65rem 0.55rem 0.55rem;
            min-height: 0;
          }
          .request-step3-grid--tuyaux-elements .request-step3-card img,
          .request-step3-grid--tuyaux-elements .request-step3-card .request-step3-card-visual {
            height: 72px;
          }
          .request-step3-grid--garage-door {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.6rem;
          }
          .request-step3-grid--garage-door .request-step3-card {
            min-height: 138px;
          }
          .request-step3-grid--garage-door .request-step3-card img {
            height: 68px;
          }
          .request-step3-grid--haies {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.6rem;
          }
          .request-step3-grid--haies .request-step3-card {
            min-height: 144px;
          }
          .request-step3-grid--haies .request-step3-card img {
            height: 72px;
          }
          .request-step3-grid--store-motorise {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.6rem;
          }
          .request-step3-grid--store-motorise .request-step3-card {
            min-height: 144px;
          }
          .request-step3-grid--store-motorise .request-step3-card img,
          .request-step3-grid--store-motorise .request-step3-card .request-step3-card-visual {
            height: 72px;
          }
        }
        @media (max-width: 380px) {
          .request-step3-grid--tuyaux-elements {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-4">
        <NioSection.Content>
          <div className="request-step3-page p-3 p-md-4">
            <div className="request-step3-content">
              <div className="request-step3-step">{t('wizard.step', { n: 3 })}</div>
                                          <h2 className="request-step3-heading">
                {tw(
                  isChargementDechargementService ||
                    isMontageDemontageMeublesService ||
                    isGardeStockageMeublesService ||
                    isGardeAccompagnementEnfantsService ||
                    isDebarrasService ||
                    isDebarrasMenageNettoyageService ||
                    isLocationUtilitaireAvecChauffeurService ||
                    isMenageSurfaceMeublesService ||
                    isRepassageService ||
                    isNettoyageVitresService ||
                    isNettoyageTapisCanapesService ||
                    isOrganisationRangementService ||
                    isOuverturePorteService ||
                    isPortesSecuriseesService ||
                    isVmcService
                    ? 'Détails de votre demande'
                    : isFeuillesService
                      ? 'Précisions sur votre demande'
                      : isHaiesService
                        ? 'Demande de détails'
                        : 'Votre demande',
                )}
              </h2>

              {Array.isArray(step3Config.q1Options) && step3Config.q1Options.length > 0 && (
                <div className="mb-5">
                  <h3 className="request-step3-question">{tw(step3Config.q1)}</h3>
                  {step3Config.q1Count ? (
                    <div className="request-step3-count-list">
                      {step3Config.q1Options.map((option) => (
                        <div
                          key={option}
                          className={`request-step3-count-item ${(firstChoiceCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step3-count-controls">
                            <button
                              type="button"
                              className="request-step3-count-btn"
                              onClick={() => updateCountChoice(option, -1)}
                              disabled={(firstChoiceCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step3-count-value">{firstChoiceCounts[option] || 0}</span>
                            <button
                              type="button"
                              className="request-step3-count-btn"
                              onClick={() => updateCountChoice(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : step3Config.q1Card ? (
                    <div
                      className={
                        step3Config.q1GridVariant === 'haies'
                          ? 'request-step3-grid request-step3-grid--haies'
                          : 'request-step3-grid'
                      }
                    >
                      {step3Config.q1Options.map((option) => {
                        const optionValue = getOptionValue(option);
                        const optionImage = getOptionImage(option);
                        return (
                          <button
                            key={optionValue}
                            type="button"
                            className={`request-step3-card ${firstChoices.includes(optionValue) ? 'is-active' : ''}`}
                            onClick={() =>
                              step3Config.q1Single
                                ? chooseSingle(optionValue, setFirstChoices)
                                : toggleChoice(optionValue, setFirstChoices)
                            }
                          >
                            {optionImage ? (
                              <img src={optionImage} alt="" />
                            ) : (
                              <div className="request-step3-card-visual" aria-hidden>
                                ···
                              </div>
                            )}
                            <div className="request-step3-card-label">{tw(optionValue)}</div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className={
                        step3Config.q1Layout === 'list'
                          ? 'request-step3-list'
                          : step3Config.q1GridVariant === 'type-vehicule'
                            ? 'request-step3-grid request-step3-grid--type-vehicule'
                          : 'request-step3-grid'
                      }
                    >
                      {step3Config.q1Options.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step3-choice ${firstChoices.includes(option) ? 'is-active' : ''}`}
                          onClick={() => (step3Config.q1Single ? chooseSingle(option, setFirstChoices) : toggleChoice(option, setFirstChoices))}
                        >{tw(option)}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {step3Config.textLabel && (
                <div className="mb-5">
                  <h3 className="request-step3-question">{tw(step3Config.textLabel)}</h3>
                  <input
                    className="request-step3-input"
                    placeholder={tw(step3Config.textPlaceholder)}
                    value={quantityValue}
                    onChange={(e) => setQuantityValue(e.target.value)}
                  />
                </div>
              )}

              {shouldShowQ2 && step3Config.q2 && Array.isArray(step3Config.q2Options) && step3Config.q2Options.length > 0 && (
                <div className={step3Config.q3Options?.length ? 'mb-5' : ''}>
                  <h3 className="request-step3-question">{tw(step3Config.q2)}</h3>
                  {step3Config.q2Select ? (
                    <select
                      className="request-step3-input"
                      value={secondChoices[0] || ''}
                      onChange={(e) => setSecondChoices(e.target.value ? [e.target.value] : [])}
                    >
                      <option value="" disabled>{tw(step3Config.q2SelectPlaceholder || 'Sélectionnez une réponse')}</option>
                      {step3Config.q2Options.map((option) => (
                        <option key={option} value={option}>{tw(option)}</option>
                      ))}
                    </select>
                  ) : (
                    <div
                      className={
                        step3Config.q2Layout === 'list'
                          ? 'request-step3-list'
                          : step3Config.q2GridVariant === 'tuyaux-elements'
                            ? 'request-step3-grid request-step3-grid--tuyaux-elements'
                            : step3Config.q2GridVariant === 'garage-door'
                              ? 'request-step3-grid request-step3-grid--garage-door'
                            : step3Config.q2GridVariant === 'store-motorise'
                              ? 'request-step3-grid request-step3-grid--store-motorise'
                            : step3Config.q2GridVariant === 'type-vehicule'
                              ? 'request-step3-grid request-step3-grid--type-vehicule'
                            : 'request-step3-grid'
                      }
                    >
                      {step3Config.q2Options.map((option) => {
                        const optionValue = getOptionValue(option);
                        if (step3Config.q2Count) {
                          return (
                            <div
                              key={optionValue}
                              className={`request-step3-count-item ${(secondChoiceCounts[optionValue] || 0) > 0 ? 'is-active' : ''}`}
                            >
                              <span>{tw(optionValue)}</span>
                              <div className="request-step3-count-controls">
                                <button
                                  type="button"
                                  className="request-step3-count-btn"
                                  onClick={() => updateSecondCountChoice(optionValue, -1)}
                                  disabled={(secondChoiceCounts[optionValue] || 0) === 0}
                                >
                                  -
                                </button>
                                <span className="request-step3-count-value">{secondChoiceCounts[optionValue] || 0}</span>
                                <button
                                  type="button"
                                  className="request-step3-count-btn"
                                  onClick={() => updateSecondCountChoice(optionValue, 1)}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          );
                        }
                        if (step3Config.q2Card) {
                          const optionImage = getOptionImage(option);
                          return (
                            <button
                              key={optionValue}
                              type="button"
                              className={`request-step3-card ${isHomeApplianceService ? 'request-step3-card--appliance' : ''} ${isMasticService ? 'request-step3-card--mastic' : ''} ${secondChoices.includes(optionValue) ? 'is-active' : ''}`}
                              onClick={() => (step3Config.q2Single ? chooseSingle(optionValue, setSecondChoices) : toggleChoice(optionValue, setSecondChoices))}
                            >
                              {optionImage ? (
                                <img src={optionImage} alt="" />
                              ) : (
                                <div className="request-step3-card-visual" aria-hidden>
                                  ···
                                </div>
                              )}
                              <div className="request-step3-card-label">{tw(optionValue)}</div>
                            </button>
                          );
                        }
                        return (
                          <button
                            key={optionValue}
                            type="button"
                            className={`request-step3-choice ${secondChoices.includes(optionValue) ? 'is-active' : ''}`}
                            onClick={() => (step3Config.q2Single ? chooseSingle(optionValue, setSecondChoices) : toggleChoice(optionValue, setSecondChoices))}
                          >{tw(optionValue)}</button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {shouldShowQ2 && step3Config.q2 && Array.isArray(step3Config.q2Options) && step3Config.q2Options.length === 0 && (
                <div className="mb-5">
                  <h3 className="request-step3-question">{tw(step3Config.q2)}</h3>
                  <input
                    className="request-step3-input"
                    placeholder={tw(step3Config.q2TextPlaceholder || '')}
                    value={secondChoices[0] || ''}
                    onChange={(e) => setSecondChoices(e.target.value ? [e.target.value] : [])}
                  />
                </div>
              )}

              {step3Config.q3 && Array.isArray(step3Config.q3Options) && step3Config.q3Options.length > 0 && (
                <div>
                  <h3 className="request-step3-question">{tw(step3Config.q3)}</h3>
                  {step3Config.q3Card ? (
                    <div className="request-step3-grid">
                      {step3Config.q3Options.map((option) => {
                        const optionValue = getOptionValue(option);
                        const optionImage = getOptionImage(option);
                        return (
                          <button
                            key={optionValue}
                            type="button"
                            className={`request-step3-card ${thirdChoices.includes(optionValue) ? 'is-active' : ''}`}
                            onClick={() => (step3Config.q3Single ? chooseSingle(optionValue, setThirdChoices) : toggleChoice(optionValue, setThirdChoices))}
                          >
                            {optionImage ? (
                              <img src={optionImage} alt="" />
                            ) : (
                              <div className="request-step3-card-visual" aria-hidden>
                                ···
                              </div>
                            )}
                            <div className="request-step3-card-label">{tw(optionValue)}</div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="request-step3-grid">
                      {step3Config.q3Options.map((option) => {
                        const optionValue = getOptionValue(option);
                        return (
                          <button
                            key={optionValue}
                            type="button"
                            className={`request-step3-choice ${thirdChoices.includes(optionValue) ? 'is-active' : ''}`}
                            onClick={() => (step3Config.q3Single ? chooseSingle(optionValue, setThirdChoices) : toggleChoice(optionValue, setThirdChoices))}
                          >{tw(optionValue)}</button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {step3Config.q3 && Array.isArray(step3Config.q3Options) && step3Config.q3Options.length === 0 && (
                <div>
                  <h3 className="request-step3-question">{tw(step3Config.q3)}</h3>
                  <input
                    className="request-step3-input"
                    value={thirdChoices[0] || ''}
                    onChange={(e) => setThirdChoices(e.target.value ? [e.target.value] : [])}
                  />
                </div>
              )}

              {step3Config.q4 && Array.isArray(step3Config.q4Options) && step3Config.q4Options.length > 0 && (
                <div className="mb-5">
                  <h3 className="request-step3-question">{tw(step3Config.q4)}</h3>
                  <div className={step3Config.q4Layout === 'list' ? 'request-step3-list' : 'request-step3-grid'}>
                    {step3Config.q4Options.map((option) => {
                      const optionValue = getOptionValue(option);
                      return (
                        <button
                          key={optionValue}
                          type="button"
                          className={`request-step3-choice ${fourthChoices.includes(optionValue) ? 'is-active' : ''}`}
                          onClick={() =>
                            step3Config.q4Single ? chooseSingle(optionValue, setFourthChoices) : toggleChoice(optionValue, setFourthChoices)
                          }
                        >{tw(optionValue)}</button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="request-step3-footer">
              <NioButton
                className="btn btn-primary"
                label={t('common.back')}
                onClick={() => navigate(-1)}
              />
              <NioButton
                className="btn btn-primary"
                label={t('common.next')}
                onClick={() =>
                  navigate(`/service/${serviceId}/request-demand-step4`, {
                    state: {
                      selectedSubService,
                      parentServiceTitle,
                      projectTitle,
                      requestDetails: {
                        firstChoices,
                        firstChoiceCounts,
                        quantityValue,
                        secondChoices,
                        secondChoiceCounts,
                        thirdChoices,
                        fourthChoices,
                      },
                    },
                  })
                }
              />
            </div>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default RequestDemandDetails;
